import {create} from 'zustand';

import {Players, Player, TeamPlayer} from '../types';

export type State = {
  view: 'pitch' | 'list';
  setView: (view: 'pitch' | 'list') => void;
  formation: Formation;
  overallCost: number;
  error: string;
  numberOfPlayers: number;
  players: Players;
  substitutes: [Player | null, Player | null, Player | null, Player | null];
  indicatorPosition: {
    position: Player['position'];
    index: number;
    substitute?: boolean;
  };
  playerStatsModal:
    | {
        context: 'team-player';
        player: TeamPlayer;
        action: 'remove' | 'add';
        isSubstitute?: boolean;
      }
    | {
        context: 'lineup-player';
        player: Player;
        action: 'remove';
        isSubstitute?: boolean;
      }
    | null;
  isDrawerOpen: boolean;

  openDrawer: () => void;
  closeDrawer: () => void;
  setPlayerStatsModal: (modalState: State['playerStatsModal']) => void;
  addPlayer(player: Player): void;
  removePlayer(player: Player, isSubstitute?: boolean): void;
  selectCaptain(player: Player, isSubstitute?: boolean): void;
  selectViceCaptain(player: Player, isSubstitute?: boolean): void;
  changeFormation(formation: Formation): void;
  setIndicatorPosition(indicatorPosition: State['indicatorPosition']): void;
  setState: (
    state: Pick<
      State,
      'overallCost' | 'players' | 'formation' | 'numberOfPlayers' | 'substitutes' | 'indicatorPosition'
    >,
  ) => void;

  reset: () => void;
};

function add(prevState: State, player: Player): State {
  const position = player.position;
  const positionIndex = getIndexFromPosition(position);
  const index = prevState.players[positionIndex].findIndex((p) => p?.id === player.id);
  if (index !== -1) return {...prevState, error: 'Player is already added'};
  const playerToBeReplaced: Player | null = prevState.indicatorPosition.substitute
    ? prevState.substitutes[prevState.indicatorPosition.index]
    : prevState.players[positionIndex][prevState.indicatorPosition.index];
  let overallCost;
  let numberOfPlayers;
  if (playerToBeReplaced) {
    overallCost = prevState.overallCost - playerToBeReplaced.price + player.price;
    numberOfPlayers = prevState.numberOfPlayers;
  } else {
    overallCost = prevState.overallCost + player.price;
    numberOfPlayers = prevState.numberOfPlayers + 1;
  }

  const indicatorPosition = getNextIndicatorPosition(prevState.formation, prevState.indicatorPosition);

  return {
    ...prevState,
    overallCost,
    numberOfPlayers,
    indicatorPosition,
    players: prevState.indicatorPosition.substitute
      ? prevState.players
      : (prevState.players.map((playersArr, idx) =>
          idx === positionIndex
            ? [
                ...playersArr.slice(0, prevState.indicatorPosition.index),
                player,
                ...playersArr.slice(prevState.indicatorPosition.index + 1),
              ]
            : playersArr,
        ) as State['players']),
    substitutes: prevState.indicatorPosition.substitute
      ? ([
          ...prevState.substitutes.slice(0, getIndexFromPosition(player.position)),
          player,
          ...prevState.substitutes.slice(getIndexFromPosition(player.position) + 1),
        ] as State['substitutes'])
      : prevState.substitutes,
  };
}

function remove(prevState: State, player: Player, isSubstitute?: boolean): State {
  const positionIndex = getIndexFromPosition(player.position);
  if (isSubstitute)
    return {
      ...prevState,
      numberOfPlayers: prevState.numberOfPlayers - 1,
      overallCost: prevState.overallCost - player.price,
      substitutes: [
        ...prevState.substitutes.slice(0, positionIndex),
        null,
        ...prevState.substitutes.slice(positionIndex + 1),
      ] as State['substitutes'],
      indicatorPosition: {
        position: player.position,
        substitute: true,
        index: positionIndex,
      },
    };
  const playerToBeRemovedIndex = prevState.players[positionIndex].findIndex(
    (linePlayer) => linePlayer?.id === player.id,
  );
  return {
    ...prevState,
    numberOfPlayers: prevState.numberOfPlayers - 1,
    overallCost: prevState.overallCost - player.price,
    players: [
      ...prevState.players.slice(0, positionIndex),
      [
        ...prevState.players[positionIndex].slice(0, playerToBeRemovedIndex),
        null,
        ...prevState.players[positionIndex].slice(playerToBeRemovedIndex + 1),
      ],
      ...prevState.players.slice(positionIndex + 1),
    ] as Players,
    indicatorPosition: {position: player.position, index: playerToBeRemovedIndex},
  };
}

function selectCaptain(prevState: State, player: Player, isSubstitute?: boolean): State {
  const positionIndex = getIndexFromPosition(player.position);
  // 1. Remove prev captain if exists
  const subsWithoutCaptain = prevState.substitutes.map((sub) =>
    sub ? {...sub, is_captain: 0} : null,
  ) as State['substitutes'];
  const playersWithoutCaptain = prevState.players.map((line) =>
    line.map((player) => (player ? {...player, is_captain: 0} : null)),
  ) as State['players'];

  // 2. Mark new captain
  if (isSubstitute)
    return {
      ...prevState,
      players: playersWithoutCaptain,
      substitutes: [
        ...subsWithoutCaptain.slice(0, positionIndex),
        {...player, is_captain: 1, is_vice_captain: 0},
        ...subsWithoutCaptain.slice(positionIndex + 1),
      ] as State['substitutes'],
      indicatorPosition: {
        position: player.position,
        substitute: true,
        index: positionIndex,
      },
    };
  const playerToBeCaptainIndex = prevState.players[positionIndex].findIndex(
    (linePlayer) => linePlayer?.id === player.id,
  );
  return {
    ...prevState,
    substitutes: subsWithoutCaptain,
    players: [
      ...playersWithoutCaptain.slice(0, positionIndex),
      [
        ...playersWithoutCaptain[positionIndex].slice(0, playerToBeCaptainIndex),
        {...player, is_captain: 1, is_vice_captain: 0},
        ...playersWithoutCaptain[positionIndex].slice(playerToBeCaptainIndex + 1),
      ],
      ...playersWithoutCaptain.slice(positionIndex + 1),
    ] as Players,
    indicatorPosition: {position: player.position, index: playerToBeCaptainIndex},
  };
}

function selectViceCaptain(prevState: State, player: Player, isSubstitute?: boolean): State {
  const positionIndex = getIndexFromPosition(player.position);
  // 1. Remove prev vice-captain if exists
  const subsWithoutViceCaptain = prevState.substitutes.map((sub) =>
    sub ? {...sub, is_vice_captain: 0} : null,
  ) as State['substitutes'];
  const playersWithoutViceCaptain = prevState.players.map((line) =>
    line.map((player) => (player ? {...player, is_vice_captain: 0} : null)),
  ) as State['players'];

  // 2. Mark new vice captain
  if (isSubstitute)
    return {
      ...prevState,
      players: playersWithoutViceCaptain,
      substitutes: [
        ...subsWithoutViceCaptain.slice(0, positionIndex),
        {...player, is_vice_captain: 1, is_captain: 0},
        ...subsWithoutViceCaptain.slice(positionIndex + 1),
      ] as State['substitutes'],
      indicatorPosition: {
        position: player.position,
        substitute: true,
        index: positionIndex,
      },
    };
  const playerToBeViceCaptainIndex = prevState.players[positionIndex].findIndex(
    (linePlayer) => linePlayer?.id === player.id,
  );
  return {
    ...prevState,
    substitutes: subsWithoutViceCaptain,
    players: [
      ...playersWithoutViceCaptain.slice(0, positionIndex),
      [
        ...playersWithoutViceCaptain[positionIndex].slice(0, playerToBeViceCaptainIndex),
        {...player, is_vice_captain: 1, is_captain: 0},
        ...playersWithoutViceCaptain[positionIndex].slice(playerToBeViceCaptainIndex + 1),
      ],
      ...playersWithoutViceCaptain.slice(positionIndex + 1),
    ] as Players,
    indicatorPosition: {position: player.position, index: playerToBeViceCaptainIndex},
  };
}

function changeFormation(prevState: State, formation: Formation): State {
  const newPlayers = makePlayers(formation, prevState.players);
  const {newCost, newNumberOfPlayers} = [...newPlayers, prevState.substitutes]
    .flatMap((row) => row)
    .reduce(
      (prev, curr) => ({
        newCost: prev.newCost + (curr ? curr?.price : 0),
        newNumberOfPlayers: prev.newNumberOfPlayers + (curr ? 1 : 0),
      }),
      {newCost: 0, newNumberOfPlayers: 0},
    );
  return {
    ...prevState,
    formation: formation,
    players: newPlayers,
    overallCost: newCost,
    numberOfPlayers: newNumberOfPlayers,
  };
}

const initialState: Pick<
  State,
  | 'overallCost'
  | 'players'
  | 'substitutes'
  | 'formation'
  | 'numberOfPlayers'
  | 'indicatorPosition'
  | 'error'
  | 'playerStatsModal'
  | 'view'
  | 'isDrawerOpen'
> = {
  overallCost: 0,
  players: [[null], [null, null, null, null], [null, null, null, null], [null, null]],
  substitutes: [null, null, null, null],
  formation: '4-4-2',
  numberOfPlayers: 0,
  indicatorPosition: {position: 'Goalkeeper', index: 0},
  error: '',
  playerStatsModal: null,
  view: 'pitch',
  isDrawerOpen: false,
};

export const useLineupStore = create<State>((set) => ({
  ...initialState,
  openDrawer: () => set({isDrawerOpen: true}),
  closeDrawer: () => set({isDrawerOpen: false}),
  setView: (view: 'pitch' | 'list') => set({view}),
  setPlayerStatsModal: (modalState) =>
    set({
      playerStatsModal: modalState,
    }),
  addPlayer: (player: Player) => set((state) => add(state, player)),
  removePlayer: (player: Player, isSubstitute: boolean) => set((state) => remove(state, player, isSubstitute)),
  changeFormation: (formation) => set((state) => changeFormation(state, formation)),
  selectCaptain: (player: Player, isSubstitute?: boolean) => set((state) => selectCaptain(state, player, isSubstitute)),
  selectViceCaptain: (player: Player, isSubstitute?: boolean) =>
    set((state) => selectViceCaptain(state, player, isSubstitute)),
  setIndicatorPosition: (indicatorPosition: State['indicatorPosition']) => set({indicatorPosition}),
  setState: (state) => set(state),
  reset: () => set({...initialState}),
}));

export function getIndexFromPosition(position: Player['position']) {
  switch (position) {
    case 'Goalkeeper':
      return 0;
    case 'Defender':
      return 1;
    case 'Midfielder':
      return 2;
    case 'Attacker':
      return 3;
  }
}

export function getPositionFromIndex(index: 0 | 1 | 2 | 3): Player['position'] {
  switch (index) {
    case 0:
      return 'Goalkeeper';
    case 1:
      return 'Defender';
    case 2:
      return 'Midfielder';
    case 3:
      return 'Attacker';
  }
}

function makePlayers(formation: Formation, prevPlayers: Players) {
  const gk = prevPlayers[0];
  const defmidatt = formation
    .split('-')
    .map(
      (row, rowIndex) =>
        Array.from({length: Number(row)}).map(
          (_, playerIndex) => prevPlayers[rowIndex + 1][playerIndex] ?? null,
        ) as Player[],
    );
  return [gk, ...defmidatt] as [(Player | null)[], (Player | null)[], (Player | null)[], (Player | null)[]];
}

function getNextIndicatorPosition(
  formation: string,
  indicatorPosition: State['indicatorPosition'],
): State['indicatorPosition'] {
  const noOfPlayers = [1, ...formation.split('-').map(Number)];
  const currentLineIndex = getIndexFromPosition(indicatorPosition.position);
  if (indicatorPosition.substitute)
    if (indicatorPosition.position === 'Attacker')
      return {
        position: 'Goalkeeper',
        index: 0,
        substitute: false,
      };
    else
      return {
        position: getPositionFromIndex((indicatorPosition.index + 1) as 0 | 1 | 2 | 3),
        substitute: true,
        index: indicatorPosition.index + 1,
      };
  if (noOfPlayers[currentLineIndex] > indicatorPosition.index + 1)
    return {
      ...indicatorPosition,
      index: indicatorPosition.index + 1,
    };
  if (noOfPlayers[currentLineIndex] - 1 === indicatorPosition.index && indicatorPosition.position === 'Attacker')
    return {
      index: 0,
      position: 'Goalkeeper',
      substitute: true,
    };

  return {
    index: 0,
    position: getPositionFromIndex((currentLineIndex + 1) as 0 | 1 | 2 | 3),
    substitute: false,
  };
}
